/*
 * nt-cm-layer.js — Display-only centimetre skin for DTF "by size" inputs.
 *
 * The DTF pricing pipeline works entirely in INCHES: the real size inputs
 * (.widthHeight__value / .widthHeight__value__) are the source of truth and are
 * read/written/clamped in inches all over bySize_popular.js and the section's
 * inline script. To show cm WITHOUT touching (and risking) that pricing logic,
 * we hide each real inch input and overlay a visible cm input that two-way syncs:
 *
 *   cm proxy -> real : convert cm/2.54 -> inches, write to the real field, and
 *                      re-fire the same DOM events the real field would get from
 *                      a user typing. The inch pipeline then recalculates exactly
 *                      as before — it cannot tell the difference.
 *   real -> cm proxy : whenever the pipeline writes the real field (clamp,
 *                      popular-size select, smart-sizing, +/- buttons, upload
 *                      auto-fill), reflect inches*2.54 back into the proxy.
 *
 * Internals stay in inches; only the displayed/entered unit is cm.
 */
( function () {
  if ( window.__ntCmLayerInit ) return;
  window.__ntCmLayerInit = true;

  var IN2CM = 2.54;
  var PROXY_CLASS = 'nt-cm-proxy';
  var REAL_SELECTOR = '.widthHeight__value, .widthHeight__value__';
  var syncing = false;

  // --- TEMP DIAGNOSTIC (remove once the auto-fill timing is fixed) ----------
  // Records a deduped, timestamped timeline of the ruler + custom-field state to
  // window.__ntLog so an upload's fill gap can be inspected AFTER the fact (live
  // inspection keeps missing the gap; injected recorders die on reload — baking
  // it into the asset survives reloads). Each push is skipped if it repeats the
  // previous signature for that source, so the log stays short and shows only
  // state transitions.
  window.__ntLog = window.__ntLog || [];
  var __ntBoot = Date.now();
  var __ntLastSig = {};
  function logTL( src, obj ) {
    try {
      var sig = src + '|' + JSON.stringify( obj );
      if ( __ntLastSig[ src ] === sig ) return;
      __ntLastSig[ src ] = sig;
      obj.t = Date.now() - __ntBoot;
      obj.src = src;
      window.__ntLog.push( obj );
      if ( window.__ntLog.length > 500 ) window.__ntLog.shift();
    } catch ( e ) {}
  }
  // --- END TEMP DIAGNOSTIC --------------------------------------------------

  function toInch( cm ) {
    var n = parseFloat( cm );
    if ( isNaN( n ) ) n = 0;
    return n / IN2CM;
  }
  // Inches -> cm rounded to 1 decimal, trailing ".0" dropped for a clean display.
  function fmtCm( inches ) {
    var n = parseFloat( inches );
    if ( isNaN( n ) ) n = 0;
    return String( Math.round( n * IN2CM * 10 ) / 10 );
  }

  function copyStyle( realEl, proxyEl ) {
    var cs = window.getComputedStyle( realEl );
    var props = [
      'textAlign', 'border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft',
      'borderRadius', 'outline', 'padding', 'paddingTop', 'paddingRight', 'paddingBottom',
      'paddingLeft', 'margin', 'color', 'fontFamily', 'fontSize', 'fontWeight', 'fontStyle',
      'lineHeight', 'letterSpacing', 'boxSizing', 'appearance', 'webkitAppearance'
    ];
    props.forEach( function ( p ) {
      try { proxyEl.style[ p ] = cs[ p ]; } catch ( e ) {}
    } );
    // Fill the (flex) slot the real input used to occupy, responsively.
    proxyEl.style.width = '100%';
    proxyEl.style.maxWidth = '100%';
    proxyEl.style.minWidth = '0';
    proxyEl.style.flex = '1 1 auto';
    proxyEl.style.background = 'transparent';
    proxyEl.style.backgroundColor = 'transparent';
    proxyEl.style.boxShadow = 'none';
  }

  function hideReal( real ) {
    real.style.position = 'absolute';
    real.style.left = '-9999px';
    real.style.top = '0';
    real.style.width = '1px';
    real.style.height = '1px';
    real.style.opacity = '0';
    real.style.pointerEvents = 'none';
    real.setAttribute( 'tabindex', '-1' );
    real.setAttribute( 'aria-hidden', 'true' );
  }

  function onProxyInput( proxy, real ) {
    if ( syncing ) return;
    proxy.__ntLastTyped = Date.now();
    syncing = true;
    try {
      var inches = toInch( proxy.value );
      window.jQuery( real ).val( inches.toFixed( 2 ) );
      real.setAttribute( 'value', inches.toFixed( 2 ) );
    } finally {
      syncing = false;
    }
    // Drive the inch pipeline exactly as a real keystroke would.
    window.jQuery( real ).trigger( 'input' ).trigger( 'keyup' ).trigger( 'change' );
  }

  function ensureProxy( real ) {
    if ( !real || real.__ntProxied ) return;
    if ( real.classList.contains( PROXY_CLASS ) ) return;
    real.__ntProxied = true;

    var proxy = document.createElement( 'input' );
    proxy.type = 'number';
    proxy.className = PROXY_CLASS;
    proxy.setAttribute( 'inputmode', 'decimal' );
    proxy.setAttribute( 'step', '0.1' );

    var minIn = parseFloat( real.getAttribute( 'min' ) );
    var maxIn = parseFloat( real.getAttribute( 'max' ) );
    if ( !isNaN( minIn ) ) proxy.min = Math.round( minIn * IN2CM * 10 ) / 10;
    if ( !isNaN( maxIn ) ) proxy.max = Math.round( maxIn * IN2CM * 10 ) / 10;

    proxy.value = fmtCm( real.value );

    copyStyle( real, proxy );
    hideReal( real );

    if ( real.nextSibling ) real.parentNode.insertBefore( proxy, real.nextSibling );
    else real.parentNode.appendChild( proxy );

    real.__ntProxyEl = proxy;
    proxy.__ntRealEl = real;

    var handler = function () { onProxyInput( proxy, real ); };
    proxy.addEventListener( 'input', handler );
    proxy.addEventListener( 'change', handler );
  }

  function syncRealToProxy( real ) {
    var proxy = real.__ntProxyEl;
    if ( !proxy ) return;
    // Only hold off while the user is ACTIVELY typing in this proxy. Merely
    // having focus must not block updates — e.g. when an aspect-locked partner
    // dimension is recalculated programmatically (no input event), the focused
    // field still needs to reflect the new value.
    if ( document.activeElement === proxy && ( Date.now() - ( proxy.__ntLastTyped || 0 ) ) < 1000 ) return;
    var desired = fmtCm( real.value );
    if ( proxy.value !== desired ) proxy.value = desired;
  }

  // Rewrite an inch-formatted dimension substring (e.g. `11" x 11"`) to cm,
  // preserving any surrounding text (descriptive suffixes, etc.). Idempotent:
  // once converted there is no inch mark left to match. Leaves already-cm text
  // (e.g. a popular variant renamed to "10cm x 10cm") untouched.
  function inchTextToCm( text ) {
    return text.replace( /([\d.]+)\s*"\s*[x×]\s*([\d.]+)\s*"/, function ( _, a, b ) {
      var wcm = Math.round( parseFloat( a ) * IN2CM * 10 ) / 10;
      var hcm = Math.round( parseFloat( b ) * IN2CM * 10 ) / 10;
      // "Ncm x Mcm" — matches the natively cm-named variants (e.g. "8cm x 8cm")
      // so converted options don't read as "5.5 x 6 cm" beside them.
      return wcm + 'cm x ' + hcm + 'cm';
    } );
  }

  // The uploaded-design preview shows ruler dimensions via CSS
  // `content: attr(x-is)'"'`. x-is/y-is hold INCHES and are also read for
  // calculations, so we leave them alone and mirror cm into x-cm/y-cm, which a
  // CSS override renders with a " cm" suffix instead of the inch mark.
  function convertPreviewRulers() {
    var h = document.querySelectorAll( '.horizontal_direction' );
    for ( var i = 0; i < h.length; i++ ) {
      var xv = h[ i ].getAttribute( 'x-is' );
      if ( xv == null ) continue;
      var xn = parseFloat( xv );
      h[ i ].setAttribute( 'x-cm', isNaN( xn ) ? '' : ( Math.round( xn * IN2CM * 10 ) / 10 ) );
    }
    var v = document.querySelectorAll( '.verticle_direction' );
    for ( var j = 0; j < v.length; j++ ) {
      var yv = v[ j ].getAttribute( 'y-is' );
      if ( yv == null ) continue;
      var yn = parseFloat( yv );
      v[ j ].setAttribute( 'y-cm', isNaN( yn ) ? '' : ( Math.round( yn * IN2CM * 10 ) / 10 ) );
    }
  }

  // Popular-size <select>s show the size in their option text but match variants
  // on the option VALUE — so we convert only the visible text, never the value.
  function convertPopularOptions() {
    var selects = document.querySelectorAll( '.customTabelPopup__sizes__popularSelect, .popularSizes' );
    for ( var i = 0; i < selects.length; i++ ) {
      var opts = selects[ i ].options;
      for ( var j = 0; j < opts.length; j++ ) {
        var o = opts[ j ];
        var nt = inchTextToCm( o.textContent );
        if ( nt !== o.textContent ) o.textContent = nt;
      }
    }
  }

  // Belt-and-suspenders: the preview ruler reliably carries the detected design
  // size (inches) after upload. If a visible CUSTOM row's inputs are still
  // blank/zero (the pipeline's id-based fill can miss the first row at load),
  // seed them straight from the ruler and fire the pricing pipeline so the size
  // and price fill in without the user having to interact. Once per row, and
  // never overrides a value the user already entered.
  // Read a ruler dimension robustly. There are TWO ruler elements (the visible
  // one with the value + a hidden one stuck at 0), and x-is can be transiently
  // cleared mid-flow while the cm DISPLAY attribute (x-cm/y-cm) still shows the
  // size. So: try x-is>0 in the box, then x-is>0 anywhere, then fall back to the
  // displayed x-cm (cm -> inches). This directly honours "if the ruler shows a
  // size, the fields can be filled from it."
  function readRuler( box, sel, isAttr, cmAttr ) {
    var _e, _q, _n;
    _e = box.querySelectorAll( sel );
    for ( _q = 0; _q < _e.length; _q++ ) { _n = parseFloat( _e[ _q ].getAttribute( isAttr ) ); if ( _n > 0 ) return _n; }
    _e = document.querySelectorAll( sel );
    for ( _q = 0; _q < _e.length; _q++ ) { _n = parseFloat( _e[ _q ].getAttribute( isAttr ) ); if ( _n > 0 ) return _n; }
    // Fallback: displayed cm value (what the user sees) -> inches.
    for ( _q = 0; _q < _e.length; _q++ ) { _n = parseFloat( _e[ _q ].getAttribute( cmAttr ) ); if ( _n > 0 ) return _n / IN2CM; }
    return NaN;
  }

  function seedCustomFromRuler() {
    var $ = window.jQuery;
    if ( !$ ) return;
    var rows = document.querySelectorAll( '.widthHeight__custom[option-selected="custom"]' );
    logTL( 'seed-enter', { nRows: rows.length } );
    for ( var i = 0; i < rows.length; i++ ) {
      var row = rows[ i ];
      // Scope to a real upload block (skip detached/leftover rows). Do NOT
      // gate on visibility — the row is often inserted a tick before it paints,
      // and we want it pre-filled so it appears WITH its value, not after.
      var box = row.closest( 'upload-controls, uploaded-files-block' );
      if ( !box ) { logTL( 'seed-row', { i: i, act: 'skip-nobox' } ); continue; }
      var wInput = row.querySelector( '.widthHeight__item[by="width"] .widthHeight__value' );
      var hInput = row.querySelector( '.widthHeight__item[by="height"] .widthHeight__value' );
      if ( !wInput || !hInput ) { logTL( 'seed-row', { i: i, act: 'skip-noinput' } ); continue; }
      // Already has a size → leave it (don't override the pipeline or the user).
      if ( parseFloat( wInput.value ) > 0 && parseFloat( hInput.value ) > 0 ) {
        logTL( 'seed-row', { i: i, act: 'skip-filled', w: wInput.value, h: hInput.value } );
        continue;
      }
      // Don't fight active typing in either field.
      var wp = wInput.__ntProxyEl, hp = hInput.__ntProxyEl;
      if ( ( wp && document.activeElement === wp && ( Date.now() - ( wp.__ntLastTyped || 0 ) ) < 1000 ) ||
           ( hp && document.activeElement === hp && ( Date.now() - ( hp.__ntLastTyped || 0 ) ) < 1000 ) ) {
        logTL( 'seed-row', { i: i, act: 'skip-typing' } );
        continue;
      }
      var rw = readRuler( box, '.horizontal_direction', 'x-is', 'x-cm' );
      var rh = readRuler( box, '.verticle_direction', 'y-is', 'y-cm' );
      if ( !( rw > 0 ) || !( rh > 0 ) ) {
        logTL( 'seed-row', { i: i, act: 'skip-noruler', os: row.getAttribute( 'option-selected' ), vis: !!row.offsetParent, wVal: wInput.value, hVal: hInput.value, rw: isNaN( rw ) ? 'NaN' : rw, rh: isNaN( rh ) ? 'NaN' : rh } );
        continue;
      }
      // No "seeded once" flag: re-fill whenever the field is blank but the ruler
      // has a size. This covers background-removal re-rendering the row/resetting
      // the inputs after the first fill.
      $( wInput ).val( rw.toFixed( 2 ) ).attr( 'value', rw.toFixed( 2 ) );
      $( hInput ).val( rh.toFixed( 2 ) ).attr( 'value', rh.toFixed( 2 ) );
      if ( window.ntSyncSizeProxies ) window.ntSyncSizeProxies();
      $( wInput ).trigger( 'input' ).trigger( 'keyup' ).trigger( 'change' );
      logTL( 'seed-row', { i: i, act: 'FILLED', vis: !!row.offsetParent, w: rw.toFixed( 2 ), h: rh.toFixed( 2 ) } );
    }
  }

  // Exposed so the inch pipeline can mirror a value into the cm proxy in the
  // SAME synchronous call that writes it (the pipeline uses .val(), which fires
  // no event). This removes any frame/poll delay — the cm box updates the
  // instant the inch input is filled, together with the preview ruler.
  window.ntSyncSizeProxies = function () {
    if ( !window.jQuery ) return;
    var nodes = document.querySelectorAll( REAL_SELECTOR );
    for ( var i = 0; i < nodes.length; i++ ) {
      if ( nodes[ i ].classList.contains( PROXY_CLASS ) ) continue;
      ensureProxy( nodes[ i ] );
      syncRealToProxy( nodes[ i ] );
    }
  };

  function scan() {
    seedCustomFromRuler();
    var nodes = document.querySelectorAll( REAL_SELECTOR );
    for ( var i = 0; i < nodes.length; i++ ) {
      var el = nodes[ i ];
      if ( el.classList.contains( PROXY_CLASS ) ) continue;
      ensureProxy( el );
      syncRealToProxy( el );
    }
    // Self-heal: drop proxies orphaned by a section re-render (real input
    // replaced/removed) or accidentally cloned (no real reference). Without this
    // a rebuilt popup could show two cm fields per dimension.
    var proxies = document.querySelectorAll( '.' + PROXY_CLASS );
    for ( var k = 0; k < proxies.length; k++ ) {
      var px = proxies[ k ];
      if ( !px.__ntRealEl || !document.contains( px.__ntRealEl ) ) {
        if ( px.parentNode ) px.parentNode.removeChild( px );
      }
    }
    convertPopularOptions();
    convertPreviewRulers();
  }

  function boot() {
    var $ = window.jQuery;

    // Reflect pipeline writes that fire events on the real input.
    $( document ).on( 'keyup change input', REAL_SELECTOR, function () {
      if ( syncing ) return;
      if ( this.classList.contains( PROXY_CLASS ) ) return;
      syncRealToProxy( this );
    } );

    scan();

    // Light poll: catches silent .val() writes (no event) and newly injected
    // multi-upload rows; also re-syncs after async clamps.
    setInterval( scan, 250 );

    // --- TEMP DIAGNOSTIC sampler (remove with logTL) ----------------------
    // Independent of seed logic: records the raw ruler + first-custom-row field
    // state every 150ms (deduped) so the exact fill gap shows up in window.__ntLog.
    setInterval( function () {
      try {
        var hd = document.querySelectorAll( '.horizontal_direction' );
        var vd = document.querySelectorAll( '.verticle_direction' );
        var rx = 0, rxcm = 0, ry = 0, rycm = 0, n;
        for ( var a = 0; a < hd.length; a++ ) { n = parseFloat( hd[ a ].getAttribute( 'x-is' ) ); if ( n > rx ) rx = n; n = parseFloat( hd[ a ].getAttribute( 'x-cm' ) ); if ( n > rxcm ) rxcm = n; }
        for ( var b = 0; b < vd.length; b++ ) { n = parseFloat( vd[ b ].getAttribute( 'y-is' ) ); if ( n > ry ) ry = n; n = parseFloat( vd[ b ].getAttribute( 'y-cm' ) ); if ( n > rycm ) rycm = n; }
        var allRows = document.querySelectorAll( '.widthHeight__custom' );
        var customRows = document.querySelectorAll( '.widthHeight__custom[option-selected="custom"]' );
        var snap = { rX: rx, rXcm: rxcm, rY: ry, rYcm: rycm, nAll: allRows.length, nCustom: customRows.length };
        var fr = customRows[ 0 ];
        if ( fr ) {
          var w = fr.querySelector( '.widthHeight__item[by="width"] .widthHeight__value' );
          var h = fr.querySelector( '.widthHeight__item[by="height"] .widthHeight__value' );
          snap.os = fr.getAttribute( 'option-selected' );
          snap.fW = w ? w.value : 'NOIN';
          snap.fH = h ? h.value : 'NOIN';
          snap.vis = !!fr.offsetParent;
          snap.inBox = !!fr.closest( 'upload-controls, uploaded-files-block' );
        }
        logTL( 'sample', snap );
      } catch ( e ) {}
    }, 150 );
    // --- END TEMP DIAGNOSTIC sampler --------------------------------------

    // Re-copy styling on resize so the proxy keeps matching the real input.
    window.addEventListener( 'resize', function () {
      var nodes = document.querySelectorAll( REAL_SELECTOR );
      for ( var i = 0; i < nodes.length; i++ ) {
        var el = nodes[ i ];
        if ( el.classList.contains( PROXY_CLASS ) ) continue;
        if ( el.__ntProxyEl ) copyStyle( el, el.__ntProxyEl );
      }
    } );
    convertPreviewRulers();

    // Two triggers:
    //  - x-is/y-is attribute changes: the preview ruler just received the
    //    detected size, so seed the custom inputs in the SAME tick — they fill
    //    at the same instant as the rulers, with no perceptible lag.
    //  - added nodes (e.g. a second multi-upload block): debounced re-scan.
    try {
      var moTimer = null;
      var mo = new MutationObserver( function ( muts ) {
        var sawAdded = false, sawRuler = false;
        for ( var i = 0; i < muts.length; i++ ) {
          var m = muts[ i ];
          if ( m.type === 'attributes' ) sawRuler = true;
          else if ( m.addedNodes && m.addedNodes.length ) sawAdded = true;
        }
        // The ruler attribute changes in the SAME synchronous block where the
        // pipeline fills the real inputs via .val() (no event, no attr change —
        // which only the slow poll would catch). So on a ruler/row mutation:
        //  1) convert the ruler's inch attrs (x-is/y-is) into the displayed cm
        //     (x-cm/y-cm) right now. Without this the ruler text only refreshes
        //     on the 250ms scan poll, so it visibly trails the cm input fields
        //     (which are mirrored synchronously below) by a frame — the rulers
        //     and the Width/Height (cm) fields must land in the same paint,
        //  2) seed any blank custom row from the ruler, then
        //  3) mirror every real input into its cm proxy right now,
        // so the ruler text and the cm fields all update together — no lag.
        if ( sawRuler || sawAdded ) {
          if ( sawRuler ) convertPreviewRulers();
          seedCustomFromRuler();
          var nodes = document.querySelectorAll( REAL_SELECTOR );
          for ( var j = 0; j < nodes.length; j++ ) {
            if ( !nodes[ j ].classList.contains( PROXY_CLASS ) ) syncRealToProxy( nodes[ j ] );
          }
        }
        if ( sawAdded && !moTimer ) {
          moTimer = setTimeout( function () { moTimer = null; scan(); }, 80 );
        }
      } );
      mo.observe( document.body, { childList: true, subtree: true, attributes: true, attributeFilter: [ 'x-is', 'y-is' ] } );
    } catch ( e ) {}
  }

  function waitForJQuery() {
    if ( window.jQuery ) {
      window.jQuery( boot );
    } else {
      setTimeout( waitForJQuery, 50 );
    }
  }
  waitForJQuery();
} )();
